import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { riderPayrollService } from "@/services/rider-payroll";
import type { RiderPayrollListParams } from "@/services/rider-payroll";

import type {
  CreateRiderPayrollReq,
  MarkRiderPayrollPaidReq,
  UpdateRiderPayrollReq,
} from "@artisancode/api-types";

export function useRiderPayrolls(
  params?: RiderPayrollListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.riderPayrolls.list(params as Record<string, unknown>),
    queryFn: () => riderPayrollService.list(params),
    enabled: options?.enabled ?? true,
  });
}

function invalidateAfterMutation(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: queryKeys.riderPayrolls.all });
  // Payroll create/update/cancel/mark-paid link or unlink rider_sales (payrollId), and drop them from the pending-payment list.
  qc.invalidateQueries({ queryKey: queryKeys.riderSales.all });
  qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
}

export function useCreateRiderPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      body,
      idempotencyKey,
    }: {
      body: CreateRiderPayrollReq;
      idempotencyKey?: string;
    }) => riderPayrollService.create(body, idempotencyKey),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useUpdateRiderPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & UpdateRiderPayrollReq) =>
      riderPayrollService.update(id, body),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useMarkRiderPayrollPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & MarkRiderPayrollPaidReq) =>
      riderPayrollService.markPaid(id, body),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}

export function useCancelRiderPayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      riderPayrollService.cancel(id, reason),
    onSuccess: () => invalidateAfterMutation(qc),
  });
}
